<template>
  <b-card
    ref="cardRoot"
    :class="cardClass"
  >
    <div class="dashboard-chart-card__header d-flex align-items-center justify-content-between flex-wrap mb-1">
      <h5
        v-if="title"
        class="mb-0 mr-1"
      >
        {{ title }}
      </h5>
      <div
        v-else
        class="flex-grow-1"
      />

      <div
        v-if="showTypeToggle"
        class="btn-group btn-group-sm dashboard-chart-card__type-toggle"
        role="group"
        aria-label="Chart type"
      >
        <button
          type="button"
          class="btn"
          :class="localType === 'bar' ? 'btn-primary' : 'btn-outline-primary'"
          :aria-pressed="localType === 'bar' ? 'true' : 'false'"
          @click="setChartType('bar')"
        >
          Bar
        </button>
        <button
          type="button"
          class="btn"
          :class="localType === 'line' ? 'btn-primary' : 'btn-outline-primary'"
          :aria-pressed="localType === 'line' ? 'true' : 'false'"
          @click="setChartType('line')"
        >
          Line
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="text-center py-3">
      <b-spinner label="Loading..." />
    </div>

    <div v-else-if="!hasData" class="text-center text-muted py-3">
      {{ emptyText }}
    </div>

    <div
      v-else
      ref="chartWrap"
      class="dashboard-chart-card__chart-wrap"
      :class="{ 'dashboard-chart-card__chart-wrap--scroll': useCompactScroll }"
      @scroll.passive="onChartScroll"
      @pointerdown="dismissScrollHint"
    >
      <div
        class="dashboard-chart-card__chart-inner"
        :style="compactChartInnerStyle"
      >
        <vue-apex-charts
          :key="`dashboard-chart-${localType}-${chartThemeKey}-${useCompactScroll}`"
          :type="localType"
          :height="height"
          :options="resolvedOptions"
          :series="series"
        />
      </div>

      <div
        v-if="useCompactScroll && showScrollHint"
        class="dashboard-chart-card__scroll-hint"
        aria-hidden="true"
      >
        <div class="dashboard-chart-card__scroll-hint-badge">
          <img
            class="dashboard-chart-card__scroll-hint-hand"
            :src="swipeHintIcon"
            alt=""
          >
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { BCard, BSpinner } from 'bootstrap-vue'
import { applyChartDarkTheme, isDarkChartTheme } from '@/libs/chartTheme'
import {
  CHART_COMPACT_LABEL_THRESHOLD,
  buildDashboardXaxisLabels,
  getChartLabelStep,
  getCompactChartMinWidth,
  isCompactDashboardWidth,
  thinChartCategories,
} from '@/libs/dashboardChart'

// eslint-disable-next-line global-require
const swipeHintIcon = require('@/assets/images/icons/swipe-horizontal.png')

export default {
  components: {
    VueApexCharts,
    BCard,
    BSpinner,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      required: true,
    },
    height: {
      type: [Number, String],
      default: 400,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasData: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'No data in the selected date range.',
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    series: {
      type: Array,
      default: () => [],
    },
    cardClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localType: this.normalizeType(this.type),
      containerWidth: 0,
      resizeObserver: null,
      showScrollHint: false,
      scrollHintTimer: null,
      swipeHintIcon,
    }
  },
  computed: {
    isCompactViewport() {
      // Card content width — not user-agent. Works when resizing a desktop browser.
      return isCompactDashboardWidth(this.containerWidth)
    },
    categoryCount() {
      return (this.options?.xaxis?.categories || []).length
    },
    useCompactScroll() {
      return this.isCompactViewport
        && this.localType !== 'pie'
        && this.categoryCount > CHART_COMPACT_LABEL_THRESHOLD
    },
    compactChartInnerStyle() {
      if (!this.useCompactScroll) {
        return null
      }
      return {
        minWidth: `${getCompactChartMinWidth(this.categoryCount)}px`,
      }
    },
    isDarkSkin() {
      return isDarkChartTheme(this.$store)
    },
    chartThemeKey() {
      return this.isDarkSkin ? 'dark' : 'light'
    },
    showTypeToggle() {
      return this.localType === 'bar' || this.localType === 'line'
    },
    preferSharedTooltip() {
      return Boolean(this.options?.tooltip?.shared)
    },
    resolvedOptions() {
      const source = this.options || {}
      const chartType = this.localType

      if (chartType === 'pie') {
        return applyChartDarkTheme({
          ...source,
          chart: {
            ...(source.chart || {}),
            type: 'pie',
            toolbar: {
              show: false,
              ...(source.chart?.toolbar || {}),
            },
          },
          legend: {
            position: 'bottom',
            ...(source.legend || {}),
          },
          dataLabels: {
            enabled: true,
            formatter(value) {
              return `${Math.round(value)}%`
            },
            ...(source.dataLabels || {}),
          },
          tooltip: {
            ...(source.tooltip || {}),
          },
        }, this.isDarkSkin, chartType)
      }

      const shared = this.preferSharedTooltip || chartType === 'line'
      const fullCategories = source.xaxis?.categories || []
      const xaxisTitle = source.xaxis?.title?.text || ''
      const useCompactLabels = this.isCompactViewport
        && fullCategories.length > CHART_COMPACT_LABEL_THRESHOLD
      const labelStep = getChartLabelStep(fullCategories.length, {
        compact: useCompactLabels,
        xaxisTitle,
      })
      const displayCategories = thinChartCategories(fullCategories, labelStep)
      const compactLabels = useCompactLabels

      const resolved = {
        ...source,
        chart: {
          ...(source.chart || {}),
          type: chartType,
          toolbar: {
            show: false,
            ...(source.chart?.toolbar || {}),
          },
          zoom: {
            enabled: false,
            ...(source.chart?.zoom || {}),
          },
        },
        dataLabels: {
          enabled: false,
          ...(source.dataLabels || {}),
        },
        xaxis: {
          ...(source.xaxis || {}),
          categories: displayCategories,
          labels: {
            ...(source.xaxis?.labels || {}),
            ...buildDashboardXaxisLabels({ compact: compactLabels }),
          },
        },
        tooltip: {
          ...(source.tooltip || {}),
          // Line: shared + non-intersect so hovering near a point shows the label.
          // Bar multi-series (shared): same. Single bar series: intersect the column.
          shared,
          intersect: shared ? false : true,
        },
      }

      if (chartType === 'bar') {
        resolved.plotOptions = {
          ...(source.plotOptions || {}),
          bar: {
            borderRadius: 4,
            columnWidth: this.useCompactScroll ? '70%' : '55%',
            ...(source.plotOptions?.bar || {}),
          },
        }
        resolved.stroke = {
          show: true,
          width: 1,
          colors: ['transparent'],
        }
        resolved.markers = {
          size: 0,
        }
      } else {
        // Keep plotOptions.bar defined — ApexCharts still reads it when switching types.
        resolved.plotOptions = {
          ...(source.plotOptions || {}),
          bar: {
            borderRadius: 4,
            columnWidth: '55%',
            ...(source.plotOptions?.bar || {}),
          },
        }
        resolved.stroke = {
          curve: 'straight',
          width: this.useCompactScroll ? 2 : 3,
          show: true,
        }
        // Visible markers make point hover reliable for tooltips.
        resolved.markers = {
          size: this.useCompactScroll ? 2 : 4,
          hover: {
            size: this.useCompactScroll ? 5 : 6,
          },
        }
        resolved.legend = {
          position: 'top',
          ...(source.legend || {}),
        }
      }

      return applyChartDarkTheme(resolved, this.isDarkSkin, chartType)
    },
  },
  watch: {
    type(value) {
      this.localType = this.normalizeType(value)
    },
    useCompactScroll: {
      immediate: true,
      handler(enabled) {
        if (enabled) {
          this.armScrollHint()
        } else {
          this.clearScrollHintTimer()
          this.showScrollHint = false
        }
      },
    },
  },
  mounted() {
    this.setupResizeObserver()
  },
  beforeUnmount() {
    this.teardownResizeObserver()
    this.clearScrollHintTimer()
  },
  methods: {
    normalizeType(value) {
      if (value === 'pie') {
        return 'pie'
      }
      return value === 'line' ? 'line' : 'bar'
    },
    setChartType(nextType) {
      if (nextType !== 'bar' && nextType !== 'line') {
        return
      }
      this.localType = nextType
    },
    getCardElement() {
      const root = this.$refs.cardRoot
      return root?.$el || root || null
    },
    setupResizeObserver() {
      this.$nextTick(() => {
        const el = this.getCardElement()
        if (!el) {
          this.containerWidth = window.innerWidth
          return
        }

        this.containerWidth = el.getBoundingClientRect().width

        if (typeof ResizeObserver === 'undefined') {
          return
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect?.width
          if (width == null) {
            return
          }
          // Avoid noisy remounts for sub-pixel changes.
          if (Math.abs(width - this.containerWidth) < 1) {
            return
          }
          this.containerWidth = width
        })
        this.resizeObserver.observe(el)
      })
    },
    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    armScrollHint() {
      this.clearScrollHintTimer()
      this.showScrollHint = true
      this.scrollHintTimer = setTimeout(() => {
        this.showScrollHint = false
        this.scrollHintTimer = null
      }, 4500)
    },
    clearScrollHintTimer() {
      if (this.scrollHintTimer) {
        clearTimeout(this.scrollHintTimer)
        this.scrollHintTimer = null
      }
    },
    dismissScrollHint() {
      if (!this.showScrollHint) {
        return
      }
      this.clearScrollHintTimer()
      this.showScrollHint = false
    },
    onChartScroll() {
      this.dismissScrollHint()
    },
  },
}
</script>

<style scoped>
.dashboard-chart-card__header {
  gap: 0.5rem;
}

.dashboard-chart-card__type-toggle .btn {
  min-width: 3.25rem;
}

.dashboard-chart-card__chart-wrap {
  position: relative;
}

.dashboard-chart-card__chart-wrap--scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-right: -0.25rem;
  margin-left: -0.25rem;
  padding-right: 0.25rem;
  padding-left: 0.25rem;
}

.dashboard-chart-card__chart-wrap--scroll .dashboard-chart-card__chart-inner {
  width: 100%;
}

.dashboard-chart-card__scroll-hint {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.72) 0%, rgba(255, 255, 255, 0.18) 55%, transparent 75%);
}

.dashboard-chart-card__scroll-hint-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  border: 2px solid rgba(34, 41, 47, 0.18);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  animation: dashboard-chart-swipe-hint 1.4s ease-in-out infinite;
}

.dashboard-chart-card__scroll-hint-hand {
  width: 56px;
  height: auto;
  opacity: 0.95;
}

@keyframes dashboard-chart-swipe-hint {
  0%,
  100% {
    transform: translateX(-14px);
  }

  50% {
    transform: translateX(14px);
  }
}
</style>
